// Slash-command + markdown reference for comments (roadmap #6). The commands themselves are resolved
// SERVER-SIDE (eventsmagic-api `boundary/commentGames.ts`) so random outcomes are shared by every viewer;
// this module is only the user-facing HELP (shown by CommentComposerHelp) plus the client-side detector for
// `/help`, which opens that help panel instead of posting a "/help" comment.

export interface CommentCommandHelp {
  /** A copy-pasteable example of the command / syntax (shown in the picker). */
  example: string;
  /** One-line description of what it does. */
  desc: string;
  /** What gets inserted into the composer when the entry is tapped (defaults to `example`). */
  insert?: string;
}

// The inline commands a guest or host can type. Keep in sync with `resolveCommentGame` (eventsmagic-api).
export const COMMENT_COMMANDS: CommentCommandHelp[] = [
  { example: '/flip', desc: 'Flip a coin — heads or tails.' },
  { example: '/roll 2d6', desc: 'Roll dice. Also /roll d20, /roll 3d10.' },
  { example: '/random 1-100', desc: 'Random number in a range (or /random 100).' },
  { example: '/choose tea, coffee', desc: 'Pick one option at random.' },
  { example: '/8ball Will it rain?', desc: 'Ask the magic 8-ball a question.' },
  { example: '/shrug', desc: 'Adds ¯\\_(ツ)_/¯ (optionally after your message).' },
  { example: '/tableflip', desc: '(╯°□°)╯︵ ┻━┻' },
  { example: '/unflip', desc: '┬─┬ ノ( ゜-゜ノ)' },
  { example: '/lenny', desc: '( ͡° ͜ʖ ͡°)' },
];

// Markdown formatting a comment supports (rendered by CommentBody). Raw HTML is NOT allowed — it shows as text.
export const COMMENT_FORMATTING: CommentCommandHelp[] = [
  { example: '**bold**   _italic_', desc: 'Emphasis.' },
  { example: '- item', desc: 'Bulleted list (one per line).' },
  { example: '> quote', desc: 'Blockquote.' },
  { example: '`code`', desc: 'Inline code.' },
  { example: 'https://…', desc: 'Links are clickable; YouTube/Vimeo/TED links show a player.', insert: 'https://' },
];

// Typing any of these on its own opens the help panel rather than posting it.
const HELP_COMMANDS = new Set(['/help', '/commands', '/?', '/h']);

/** True when the text is JUST a help request — the composer should open the help panel instead of posting. */
export const isHelpCommand = (text: string): boolean => HELP_COMMANDS.has((text || '').trim().toLowerCase());
